import naja from 'naja';

declare global {
    interface Window {
        dataLayer: any[];
        gtag?: (...args: any[]) => void;
    }
}

class GtmTracking {
    private gtmDataAttribute = 'data-gtm-view-item-list';
    private gtmViewItemAttribute = 'data-gtm-view-item';
    private gtmAddToCartAttribute = 'data-gtm-add-to-cart';
    private gtmRemoveFromCartAttribute = 'data-gtm-remove-from-cart';
    private gtmBeginCheckoutAttribute = 'data-gtm-begin-checkout';
    private gtmAddShippingInfoAttribute = 'data-gtm-add-shipping-info';
    private gtmAddPaymentInfoAttribute = 'data-gtm-add-payment-info';
    private gtmGenerateLeadAttribute = 'data-gtm-generate-lead';
    private gtmPurchaseAttribute = 'data-gtm-purchase';
    private observer?: IntersectionObserver;
    private selectItemListenerAdded = false;

    public init(): void {
        this.setupViewItemListTracking();
        this.setupViewItemTracking();
        this.setupAddToCartTracking();
        this.setupRemoveFromCartTracking();
        this.setupBeginCheckoutTracking();
        this.setupAddShippingInfoTracking();
        this.setupAddPaymentInfoTracking();
        this.setupGenerateLeadTracking();
        this.setupPurchaseTracking();
        this.setupSelectItemTracking();
        this.bindAjaxEvents();
    }

    /**
     * Single entry point for every ecommerce event. Pushes to the dataLayer and
     * forwards the same payload to GA4 via gtag, which is configured in @layout.latte.
     */
    private pushEvent(data: any): void {
        if (!data || !data.event) return;

        // Clear previous ecommerce data
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push(data);

        if (typeof window.gtag === 'function') {
            window.gtag('event', data.event, data.ecommerce ?? {});
        }

        console.log(`GTM ${data.event} event sent:`, data);
    }

    /**
     * Reads the JSON payload from a server-rendered container and sends it once.
     * The container is removed afterwards so the event cannot fire twice.
     */
    private trackOneShot(attribute: string): void {
        document.querySelectorAll(`[${attribute}]`).forEach(element => {
            const gtmDataStr = element.getAttribute(attribute);
            if (!gtmDataStr) return;

            try {
                this.pushEvent(JSON.parse(gtmDataStr));
                element.remove();
            } catch (error) {
                console.error(`Error parsing ${attribute} data:`, error);
            }
        });
    }

    /**
     * Same as trackOneShot, but the container stays in the DOM (it is part of the
     * page content), so a processed flag guards against duplicate events.
     */
    private trackOnce(attribute: string, processedFlag: string): void {
        document.querySelectorAll(`[${attribute}]`).forEach(element => {
            if (element.getAttribute(processedFlag) === 'true') {
                return;
            }

            const gtmDataStr = element.getAttribute(attribute);
            if (!gtmDataStr) return;

            try {
                this.pushEvent(JSON.parse(gtmDataStr));
                element.setAttribute(processedFlag, 'true');
            } catch (error) {
                console.error(`Error parsing ${attribute} data:`, error);
            }
        });
    }

    private setupViewItemListTracking(): void {
        this.trackOnce(this.gtmDataAttribute, 'data-gtm-processed');
    }

    private setupViewItemTracking(): void {
        this.trackOnce(this.gtmViewItemAttribute, 'data-gtm-view-item-processed');
    }

    private setupAddToCartTracking(): void {
        this.trackOneShot(this.gtmAddToCartAttribute);
    }

    private setupRemoveFromCartTracking(): void {
        this.trackOneShot(this.gtmRemoveFromCartAttribute);
    }

    private setupBeginCheckoutTracking(): void {
        this.trackOneShot(this.gtmBeginCheckoutAttribute);
    }

    private setupAddShippingInfoTracking(): void {
        this.trackOneShot(this.gtmAddShippingInfoAttribute);
    }

    private setupAddPaymentInfoTracking(): void {
        this.trackOneShot(this.gtmAddPaymentInfoAttribute);
    }

    private setupGenerateLeadTracking(): void {
        this.trackOneShot(this.gtmGenerateLeadAttribute);
    }

    private setupPurchaseTracking(): void {
        this.trackOneShot(this.gtmPurchaseAttribute);
    }

    private setupSelectItemTracking(): void {
        // Only add the event listener once
        if (!this.selectItemListenerAdded) {
            document.addEventListener('click', (event) => {
                const target = event.target as HTMLElement;
                const productLink = target.closest('.category-item');

                if (productLink && productLink.tagName === 'A') {
                    this.trackSelectItem(productLink as HTMLAnchorElement);
                }
            });
            this.selectItemListenerAdded = true;
        }
    }

    private trackSelectItem(productLink: HTMLAnchorElement): void {
        // Find the parent container with view_item_list data
        const listContainer = productLink.closest(`[${this.gtmDataAttribute}]`) as HTMLElement;

        if (!listContainer) return;

        const gtmDataStr = listContainer.getAttribute(this.gtmDataAttribute);
        if (!gtmDataStr) return;

        try {
            const gtmData = JSON.parse(gtmDataStr);

            // Find the product index in the list
            const allProductLinks = listContainer.querySelectorAll('.category-item');
            let productIndex = -1;

            for (let i = 0; i < allProductLinks.length; i++) {
                if (allProductLinks[i] === productLink) {
                    productIndex = i;
                    break;
                }
            }

            if (productIndex >= 0 && gtmData.ecommerce && gtmData.ecommerce.items) {
                const selectedProduct = gtmData.ecommerce.items[productIndex];

                if (selectedProduct) {
                    this.pushEvent({
                        event: 'select_item',
                        ecommerce: {
                            item_list_id: gtmData.ecommerce.item_list_id,
                            item_list_name: gtmData.ecommerce.item_list_name,
                            items: [selectedProduct]
                        }
                    });
                }
            }
        } catch (error) {
            console.error('Error tracking select_item:', error);
        }
    }

    private bindAjaxEvents(): void {
        // Listen for snippet redraws after AJAX requests
        naja.snippetHandler.addEventListener('afterUpdate', (event: { detail: { snippet: { id: string } } }) => {
            console.log('GTM: AJAX snippet updated:', event.detail.snippet.id);

            // Re-initialize tracking after product list updates
            if (event.detail.snippet.id === 'products' ||
                event.detail.snippet.id.includes('category') ||
                event.detail.snippet.id.includes('search')) {

                // Small delay to ensure DOM is updated
                setTimeout(() => {
                    this.setupViewItemListTracking();
                    // Don't re-setup select item tracking as it's a global listener
                }, 100);
            }

            // Check for cart events after AJAX notifications
            if (event.detail.snippet.id === 'snippet--ajaxNotification') {
                console.log('GTM: Processing ajaxNotification snippet update');
                // Increase delay and ensure DOM is ready
                setTimeout(() => {
                    console.log('GTM: Re-initializing cart tracking');
                    this.setupAddToCartTracking();
                    this.setupRemoveFromCartTracking();
                    this.setupBeginCheckoutTracking();
                    this.setupAddShippingInfoTracking();
                    this.setupAddPaymentInfoTracking();
                }, 150);
            }
        });
    }
}

export default GtmTracking;
